import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { expertDashboardApi } from '../../../lib/http';
import { Schedule, WEEKDAY_SHORT_NAMES } from '../../../types/working-hours';
import ScheduleFormPanel from './ScheduleFormPanel';

interface SeasonalSchedulesTabProps {
  schedules: Schedule[];
  defaultSchedule?: Schedule;
  isLoading: boolean;
}

const SeasonalSchedulesTab: React.FC<SeasonalSchedulesTabProps> = ({
  schedules,
  defaultSchedule,
  isLoading,
}) => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => expertDashboardApi.schedules.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expert-schedules'] });
      toast.success('Schedule deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete schedule');
    },
  });

  const handleAdd = () => {
    setEditingSchedule(null);
    setIsFormOpen(true);
  };

  const handleEdit = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    setIsFormOpen(true);
  };

  const handleDelete = (schedule: Schedule) => {
    if (window.confirm(`Delete "${schedule.name}"? This cannot be undone.`)) {
      deleteMutation.mutate(schedule.id);
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingSchedule(null);
    queryClient.invalidateQueries({ queryKey: ['expert-schedules'] });
  };

  const formatDateRange = (schedule: Schedule) => {
    if (!schedule.starts_at || !schedule.ends_at) return 'Always active';

    const start = new Date(schedule.starts_at);
    const end = new Date(schedule.ends_at);

    const formatDate = (date: Date) =>
      date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const getWorkingDaysSummary = (schedule: Schedule) => {
    const days = new Set(schedule.working_hours.map(wh => wh.weekday));
    if (days.size === 0) return 'No working days';
    if (days.size === 7) return 'Every day';
    if (days.size === 5 && !days.has(6) && !days.has(7)) return 'Mon - Fri';

    return Array.from(days)
      .sort((a, b) => a - b)
      .map(d => WEEKDAY_SHORT_NAMES[d])
      .join(', ');
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2].map(i => (
          <div key={i} className="h-32 bg-gray-100 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Info Banner - Compact on mobile */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4">
        <div className="flex gap-2 sm:gap-3">
          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <p className="text-xs sm:text-sm text-blue-800">
            <strong>Seasonal schedules</strong> override regular hours during specific dates.
            <span className="hidden sm:inline"> When multiple schedules overlap, the one with higher priority takes precedence.</span>
          </p>
        </div>
      </div>

      {/* Default Schedule Card */}
      {defaultSchedule && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl sm:rounded-2xl p-4 sm:p-6">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-purple-100 text-purple-800">
                  Default
                </span>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{defaultSchedule.name}</h3>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">Active when no seasonal schedule applies</p>
              <div className="mt-2 sm:mt-3 flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                <span>{getWorkingDaysSummary(defaultSchedule)}</span>
                <span className="text-gray-300">•</span>
                <span>{defaultSchedule.total_weekly_hours}h/wk</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seasonal Schedules List */}
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Seasonal</h3>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-purple-600 text-white text-sm rounded-xl hover:bg-purple-700 transition-all shadow-sm active:scale-[0.98]"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden min-[400px]:inline">Add</span>
          </button>
        </div>

        {schedules.length === 0 ? (
          <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl sm:rounded-2xl p-6 sm:p-12 text-center">
            <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h4 className="text-base sm:text-lg font-medium text-gray-600 mb-1 sm:mb-2">No seasonal schedules</h4>
            <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4 max-w-xs mx-auto">
              Create schedules for holidays or different availability periods.
            </p>
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-xl hover:bg-purple-700 transition-all active:scale-[0.98]"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Schedule
            </button>
          </div>
        ) : (
          <div className="grid gap-3 sm:gap-4">
            {schedules.map((schedule) => (
              <div
                key={schedule.id}
                className={`bg-white border rounded-xl sm:rounded-2xl p-4 sm:p-6 transition-all hover:shadow-md ${
                  schedule.status === 'active'
                    ? 'border-green-200 ring-1 ring-green-100'
                    : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                      <h4 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{schedule.name}</h4>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                          schedule.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : schedule.status === 'upcoming'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {schedule.status === 'active' && '● '}
                        {schedule.status.charAt(0).toUpperCase() + schedule.status.slice(1)}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1">{formatDateRange(schedule)}</p>
                    <div className="mt-2 sm:mt-3 flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                      <span>{getWorkingDaysSummary(schedule)}</span>
                      <span className="text-gray-300">•</span>
                      <span>{schedule.total_weekly_hours}h/wk</span>
                      {schedule.priority > 0 && (
                        <>
                          <span className="hidden sm:inline text-gray-300">•</span>
                          <span className="hidden sm:inline text-gray-400">P{schedule.priority}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleEdit(schedule)}
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit schedule"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(schedule)}
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete schedule"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Panel */}
      <ScheduleFormPanel
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingSchedule(null);
        }}
        onSuccess={handleFormSuccess}
        editSchedule={editingSchedule}
      />
    </div>
  );
};

export default SeasonalSchedulesTab;
